'use client';

import { LoadingImage } from '@modules/common/components/loading-image';
import * as Sentry from '@sentry/nextjs';
import { Component, type ReactNode } from 'react';
import type { ProductImageVM } from 'types/view-models';

type Props = {
  images: ProductImageVM[];
  productTitle: string;
  children: ReactNode;
};
type State = { hasError: boolean };

// Guards the dynamically imported product image gallery. A Next.js chunk load
// failure ("module factory is not available") throws while React renders the
// lazy gallery, which would otherwise blank the entire image column with no
// fallback at all. On high-AOV prescription PDPs that reads as an outage, so we
// degrade to a static first image plus a reload affordance — the product stays
// visible and the user can recover without leaving the page.
//
// Same shape as GoogleTranslateGuard: getDerivedStateFromError flips to a
// fallback, componentDidCatch reports to Sentry.
export class ImageGalleryErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { images, productTitle } = this.props;

    return (
      <div className="relative flex w-full flex-col items-center gap-4">
        <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-ui-bg-component">
          {/* LoadingImage renders its own placeholder icon when src is
              undefined, so a product with no images still degrades gracefully. */}
          <LoadingImage
            alt={productTitle}
            fill
            sizes="(min-width: 1024px) 610px, 100vw"
            src={images[0]?.url}
          />
        </div>
        <button
          className="cursor-pointer text-sm underline hover:no-underline"
          onClick={() => window.location.reload()}
          type="button"
        >
          Reload images
        </button>
      </div>
    );
  }
}
